import React, { useRef } from 'react';
import { useTranslation } from 'utils/i18n';
import { AuthenticatedDatasetImage } from './AuthenticatedDatasetImage';
import marketplaceTranslations from './translations';

/**
 * Marketplace provider card component
 * Shows provider logo, name, description, and tags
 * Premium styling for B-esper provider
 */
export const ProviderCard = ({ 
  provider, 
  isSelected, 
  onSelect,
  loading = false
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const cardRef = useRef(null);
  
  // Check if this is B-esper provider
  const isBesper = provider.isBesper || provider.id === 'besper' || provider.name?.toLowerCase() === 'b-esper';
  const providerName = provider.name || t('providerCard.unnamedProvider', 'Unnamed Provider');
  const logoUrl = provider.metadata?.partner_profile?.logo_url;
  const description = provider.metadata?.partner_profile?.description || provider.description || t('providerCard.noDescription', 'No description available.');
  const tags = provider.metadata?.tags || [];
  const customPrimary = provider.metadata?.partner_profile?.primary_color;
  const customAccent = provider.metadata?.partner_profile?.accent_color;

  const getProviderColor = () => {
    if (customPrimary && /^#[0-9A-Fa-f]{6}$/.test(customPrimary)) {
      return { bg: customPrimary, text: '#ffffff' };
    }
    if (customAccent && /^#[0-9A-Fa-f]{6}$/.test(customAccent)) {
      return { bg: customAccent, text: '#ffffff' };
    }
    const name = (providerName || '').toLowerCase();
    const id = (provider.id || '').toLowerCase();
    const combined = name + id;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      hash = combined.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      { bg: '#022d54', text: '#ffffff' },
      { bg: '#4a90e2', text: '#ffffff' },
      { bg: '#64748b', text: '#ffffff' },
      { bg: '#475569', text: '#ffffff' },
      { bg: '#334155', text: '#ffffff' },
      { bg: '#1e293b', text: '#ffffff' },
      { bg: '#0f172a', text: '#ffffff' },
    ];
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  };
  
  // Generate logo placeholder if no logo URL
  // eslint-disable-next-line no-unused-vars
  const getLogoPlaceholder = () => {
    const initials = providerName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    const colorScheme = getProviderColor();
    
    return (
      <div 
        className="product-logo-placeholder"
        style={{
          background: colorScheme.bg,
        }}
      >
        <span 
          className="logo-initials"
          style={{
            color: colorScheme.text,
          }}
        >
          {initials}
        </span>
      </div>
    );
  };
  
  
  const handleClick = () => {
    if (!loading) {
      onSelect(provider);
    }
  };
  
  return (
    <div
      ref={cardRef}
      className={`marketplace-card ${isBesper ? 'besper-provider-card' : ''} palantir-style left-aligned ${isSelected ? 'selected' : ''} ${loading ? 'loading' : ''}`}
      onClick={handleClick}
      style={{ cursor: loading ? 'default' : 'pointer', position: 'relative' }}
    >
      {/* Provider Logo - Top Right - Full Logo for B-esper */}
      <div className="provider-logo-top-right">
        {isBesper ? (
          <div className="provider-full-logo">
            <div className="bsp-logo-wrapper">
              <div className="bsp-logo-circle">
                <span className="bsp-logo-letter">B</span>
              </div>
              <span className="bsp-logo-text">esper</span>
            </div>
          </div>
        ) : logoUrl ? (
          <div className="provider-logo-image">
            <AuthenticatedDatasetImage
              logoUrl={logoUrl}
              alt={providerName}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
        ) : (
          <div className="provider-logo-placeholder" style={{
            width: '48px',
            height: '48px',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: getProviderColor().bg
          }}>
            <span style={{
              color: getProviderColor().text,
              fontSize: '18px',
              fontWeight: '600',
              letterSpacing: '0',
              lineHeight: '1',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", "Roboto", sans-serif'
            }}>
              {providerName.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
        )}
      </div>
      
      {/* Content Wrapper - Proper flex layout */}
      <div className="provider-card-content-wrapper">
        {/* Top Content - Name and Description */}
        <div className="provider-card-top-content">
          {/* Provider Name - Left Aligned (with space for top-right logo) */}
          <div className="product-header-palantir-left">
            <h3 className={`product-name-palantir-left provider-name-with-logo ${isBesper ? 'besper-premium-name' : ''}`} title={providerName}>
              {providerName}
            </h3>
          </div>
          
          {/* Provider Description - Left Aligned */}
          <div className="product-description-container-palantir-left">
            <p className={`product-description-palantir-left ${isBesper ? 'besper-premium-description' : ''}`}>
              {description}
            </p>
          </div>
        </div>
        
        {/* Tags - Pushed to Bottom */}
        {tags.length > 0 && (
          <div className={`product-tags-container provider-card-tags-bottom ${isBesper ? 'besper-premium-tags' : ''}`}>
            {tags.slice(0, 3).map((tag, index) => (
              <span key={index} className={`product-tag-pill ${isBesper ? 'besper-premium-tag' : ''}`}>
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="product-tag-more">
                +{tags.length - 3} more
              </span>
            )}
          </div>
        )}
      </div>
      
    </div>
  );
};

